import { inject, Injectable } from '@angular/core';
import {
  catchError,
  defaultIfEmpty,
  finalize,
  from,
  Observable,
  of,
  shareReplay,
  switchMap,
  throwError,
  timeout,
} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import {
  CheckStatusResponse,
  LoginResponse,
} from '../data/models/loginResponse.model';
import { LocalDatabaseService } from './local-database.service';
import { NetworkService } from './network-service.service';
import { AppSnackService } from './snack-service.service';
import { UtilsService } from './utils.service';
import { User } from '../data/models/user.model';

@Injectable({
  providedIn: 'root',
})
export class OauthService {
  private readonly SESSION_KEY = 'session_email';
  private readonly authEp = 'auth';
  // private readonly registerFCMtokenEp = 'notifications/register-token';
  private readonly resetPasswEp = 'forgot-password';
  private readonly checkStatusEp = 'check-status';
  private readonly SYNC_TIMEOUT_MS = 3000;
  private readonly SYNC_TTL_MS = 300_000;

  private syncInFlight?: Observable<void>;
  private lastSync = 0;

  private readonly db = inject(LocalDatabaseService);
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly netSrv = inject(NetworkService);
  private readonly snackSrv = inject(AppSnackService);
  private readonly utilsSrv = inject(UtilsService);

  /**
   *
   *
   * @param {*} credential
   * @return {*}  {Observable<any>}
   * @memberof OauthService
   */
  login(creds: any): Observable<LoginResponse> {
    if (this.netSrv.isOnline()) {
      return this.http
        .post<LoginResponse>(`${environment.apiUrl}/${this.authEp}/login`, {
          // TODO: tipar creds
          email: creds.email,
          password: creds.password,
          isMobile: 1,
        })
        .pipe(
          switchMap((response: any) => {
            return from(this.processLoginResponse(response, creds));
          }
          ),
          catchError((error: any) => {
            //console.log('Login error: ', error);
            return throwError(() => error);
          })
        );
    } else {
      return from(this.verifyOfflineLogin(creds));
    }
  }

  private async processLoginResponse(
    response: LoginResponse,
    creds: any
  ): Promise<LoginResponse> {
    sessionStorage.setItem('token', JSON.stringify(response.token));
    sessionStorage.setItem(
      'token_expiresAT',
      JSON.stringify(response.expires_at)
    );
    const { hash, salt } = await this.utilsSrv.hashPassword(creds.password);
    await this.db.upsertUser({
      ...response.user,
      permissions: response.permissions ?? [],
      // Guardamos el hash y la sal, nunca la contraseña.
      passwordHash: hash,
      passwordSalt: salt,
      loggedIn: 1,
    });
    this.lastSync = Date.now();
    return response;
  }

  syncSession(): Observable<void> {
    if (!this.hasSession() || !this.netSrv.isOnline()) {
      return of(undefined);
    }

    const token = this.getToken();
    if (!token || this.isTokenExpired(token)) {
      return of(undefined);
    }

    if (Date.now() - this.lastSync < this.SYNC_TTL_MS) {
      return of(undefined);
    }

    this.syncInFlight ??= this.http
      .get<CheckStatusResponse>(
        `${environment.apiUrl}/${this.authEp}/${this.checkStatusEp}`
      )
      .pipe(
        timeout(this.SYNC_TIMEOUT_MS),
        switchMap((response) =>
          from(this.mergePermissions(response?.permissions)).pipe(
            switchMap((changed) =>
              changed
                ? from(this.notifyAccessChange(response.permissions))
                : of(undefined)
            )
          )
        ),
        catchError((error) => {
          console.warn('[syncSession] no se pudo revalidar la sesión', error);
          return of(undefined);
        }),
        defaultIfEmpty(undefined),
        finalize(() => {
          this.lastSync = Date.now();
          this.syncInFlight = undefined;
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.syncInFlight;
  }

  private async mergePermissions(
    permissions: string[] | undefined
  ): Promise<boolean> {
    if (!Array.isArray(permissions)) {
      return false;
    }

    const localUser = await this.db.getCurrentLoggedUser();
    if (!localUser) {
      return false;
    }

    if (this.permissionsKey(localUser.permissions) === this.permissionsKey(permissions)) {
      return false;
    }

    return (await this.db.updateUser(localUser.id, { permissions })) > 0;
  }

  private async notifyAccessChange(permissions: string[]): Promise<void> {
    this.snackSrv.openWithContext(
      'Tus permisos cambiaron. Se actualizó lo que podés ver en la app.',
      'warning'
    );
    if (!this.isCurrentRouteAllowed(permissions)) {
      await this.router.navigateByUrl('/inicio');
    }
  }

  private isCurrentRouteAllowed(permissions: string[]): boolean {
    const path = this.router.url.split(/[?#]/)[0].replace(/^\//, '');
    const required = this.router.config.find((r) => r.path === path)
      ?.data?.['permissions'] as unknown;

    if (!Array.isArray(required) || !required.length) {
      return true;
    }
    return permissions.some((permission) => required.includes(permission));
  }

  private permissionsKey(permissions: string[] | undefined): string {
    return [...(permissions ?? [])].sort().join(',');
  }

  private async verifyOfflineLogin(creds: any): Promise<LoginResponse> {
    const users = await this.db.getUsers();
    const user = users.find((u) => u.email === creds.email);

    if (!user) {
      throw { message: 'Credenciales incorrectas' };
    }

    let passwordIsValid = false;
    // Lógica de verificación con el nuevo sistema de hash
    if (user.passwordHash && user.passwordSalt) {
      passwordIsValid = await this.utilsSrv.verifyPassword(
        creds.password,
        user.passwordSalt,
        user.passwordHash
      );
    }

    if (passwordIsValid) {
      user.loggedIn = 1; // Mark user as logged in
      await this.db.upsertUser(user);
      return {
        user,
        token: 'no_token',
        expires_at: 0,
      } as LoginResponse;
    } else {
      throw { message: 'Credenciales incorrectas' };
    }
  }

  logout(): Observable<void> {
    // TODO: falta el logout en api
    return from(this.clearUserSession());
  }

  private async clearUserSession(): Promise<void> {
    sessionStorage.clear();
    await this.db.logoutAll();
    try {
      const user = await this.db.getLoggedUser();
      if (user) {
        user.loggedIn = 0;
        await this.db.upsertUser(user);
      }
    } catch (error) {
      console.error('Error updating user on logout', error);
    } finally {
      this.removeUser();
    }
  }

  startSession(email: string): void {
    sessionStorage.setItem(this.SESSION_KEY, email);
  }

  hasSession(): boolean {
    return (
      typeof sessionStorage !== 'undefined' &&
      !!sessionStorage.getItem(this.SESSION_KEY)
    );
  }

  getToken(): string | null {
    const tkn = sessionStorage.getItem('token');
    if (tkn) {
      return JSON.parse(tkn);
    }
    return null;
  }

  isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      return payload.exp < now;
    } catch {
      return true;
    }
  }

  async handleUnauthorized(): Promise<void> {
    try {
      this.removeUser();
      // Usamos await para asegurarnos de que la navegación se inicie correctamente.
      await this.router.navigate(['/login']);
    } catch (e) {
      console.error('Error al intentar redirigir al login:', e);
      // Incluso si la navegación falla, intentamos limpiar la sesión.
      this.removeUser();
    }
  }

  async getUser(): Promise<User | undefined> {
    return this.db.getLoggedUser();
  }

  removeUser(): void {
    sessionStorage.removeItem(this.SESSION_KEY);
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('token_expiresAT');
  }

/*   registerFCMtoken(token: string): Observable<any> {
    return this.http.post<any>(
      `${environment.apiUrl}/${this.registerFCMtokenEp}`,
      {
        fcmToken: token,
      }
    );
  } */

  forgotPassword(email: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}/${this.authEp}/${this.resetPasswEp}`, {
      email
    });
  }
}
